/**
 * ComplianceConfigTab (Plan 05-09) — V1-parity Configuración de Cumplimiento.
 *
 * Three sections (mirrors the V1 single-position screen):
 *   - Identidad de la Empresa: 7 text fields incl. Número de registro PRODHAB
 *   - Aprobaciones Legales: privacy_notice_approved + video_consent_approved checkboxes
 *   - Configuración del Sistema: retention_months + data_recipients + mode (test/live)
 *
 * Per-field red/green chips driven by the backend `bar_status` array. A banner explains
 * the candidate portal stays blocked until every required field is green. Mode toggle
 * (Select test/live) calls POST /mode and surfaces the 422 compliance_incomplete missing
 * list inline (BUG-022 — no silent failure). Editable by content_admin AND super_admin
 * (Plan 05-08 D4); the route guard (App.tsx) already blocks hr_* / hiring_manager.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/types/api";

import type { ComplianceConfig } from "./api";
import {
  useComplianceConfig,
  useSwitchComplianceMode,
  useUpdateComplianceConfig,
} from "./hooks";

const TEXT_FIELDS = [
  "company_legal_name",
  "cedula_juridica",
  "address",
  "contact_email",
  "contact_phone",
  "prodhab_number",
  "data_protection_contact",
] as const;

type FormState = {
  company_legal_name: string;
  cedula_juridica: string;
  address: string;
  contact_email: string;
  contact_phone: string;
  prodhab_number: string;
  data_protection_contact: string;
  data_recipients: string;
  retention_months: number;
  privacy_notice_approved: boolean;
  video_consent_approved: boolean;
};

function toForm(cfg: ComplianceConfig): FormState {
  return {
    company_legal_name: cfg.company_legal_name ?? "",
    cedula_juridica: cfg.cedula_juridica ?? "",
    address: cfg.address ?? "",
    contact_email: cfg.contact_email ?? "",
    contact_phone: cfg.contact_phone ?? "",
    prodhab_number: cfg.prodhab_number ?? "",
    data_protection_contact: cfg.data_protection_contact ?? "",
    data_recipients: cfg.data_recipients ?? "",
    retention_months: cfg.retention_months ?? 6,
    privacy_notice_approved: cfg.privacy_notice_approved,
    video_consent_approved: cfg.video_consent_approved,
  };
}

export function ComplianceConfigTab() {
  const { t } = useTranslation("admin-settings");
  const configQuery = useComplianceConfig();
  const updateMutation = useUpdateComplianceConfig();
  const modeMutation = useSwitchComplianceMode();

  const [form, setForm] = useState<FormState | null>(null);
  const [missing, setMissing] = useState<string[]>([]);

  // Sync local form once the singleton loads / refetches.
  useEffect(() => {
    if (configQuery.data) setForm(toForm(configQuery.data));
  }, [configQuery.data]);

  const barByField = useMemo(() => {
    const m: Record<string, "red" | "green"> = {};
    for (const e of configQuery.data?.bar_status ?? []) m[e.field] = e.status;
    return m;
  }, [configQuery.data]);

  if (configQuery.isLoading || !form || !configQuery.data) {
    return (
      <p className="text-sm text-[var(--talenta-text-muted)]">{t("config.loading")}</p>
    );
  }
  const cfg = configQuery.data;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function handleSave() {
    if (!form) return;
    try {
      await updateMutation.mutateAsync({
        company_legal_name: form.company_legal_name,
        cedula_juridica: form.cedula_juridica,
        address: form.address,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        prodhab_number: form.prodhab_number,
        data_protection_contact: form.data_protection_contact,
        data_recipients: form.data_recipients,
        retention_months: form.retention_months,
        privacy_notice_approved: form.privacy_notice_approved,
        video_consent_approved: form.video_consent_approved,
      });
      toast.success(t("config.save_success"));
    } catch {
      toast.error(t("config.save_error"));
    }
  }

  function handleDiscard() {
    if (configQuery.data) setForm(toForm(configQuery.data));
    setMissing([]);
  }

  async function handleModeChange(next: "test" | "live") {
    setMissing([]);
    try {
      await modeMutation.mutateAsync(next);
      toast.success(t("config.mode_switch_success"));
    } catch (err) {
      // 422 compliance_incomplete -> surface the missing list inline (no silent fail).
      if (err instanceof ApiError && err.status === 422) {
        const detail = err.body?.detail;
        if (detail && typeof detail === "object" && "missing" in detail) {
          setMissing((detail as { missing: string[] }).missing ?? []);
        }
        toast.error(t("config.mode_incomplete"));
      } else {
        toast.error(t("config.save_error"));
      }
    }
  }

  function Chip({ field }: { field: string }) {
    const status = barByField[field];
    if (!status) return null;
    return (
      <Badge
        data-testid={`chip-${field}`}
        className={
          status === "green"
            ? "bg-[#15803d] text-white hover:bg-[#15803d]"
            : "bg-[#dc2626] text-white hover:bg-[#dc2626]"
        }
      >
        {status === "green" ? t("config.green") : t("config.red")}
      </Badge>
    );
  }

  const saving = updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t("config.title")}</h2>
        <p className="text-sm text-[var(--talenta-text-muted)]">{t("config.subtitle")}</p>
      </div>

      {/* All-green / blocked banner */}
      <Card>
        <CardContent className="py-3">
          {cfg.can_switch_to_live ? (
            <p className="text-sm font-medium text-[#15803d]">{t("config.all_green")}</p>
          ) : (
            <p className="text-sm font-medium text-[#dc2626]">{t("config.blocked_banner")}</p>
          )}
        </CardContent>
      </Card>

      {/* Section: Identidad de la Empresa */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold">{t("config.section_identity")}</h3>
        {TEXT_FIELDS.map((field) => (
          <div key={field} className="space-y-1">
            <div className="flex items-center gap-2">
              <Label htmlFor={`cfg-${field}`}>{t(`config.field.${field}`)}</Label>
              <Chip field={field} />
            </div>
            <Input
              id={`cfg-${field}`}
              type={field === "contact_email" ? "email" : "text"}
              value={form[field]}
              placeholder={
                barByField[field] === "red" ? t("config.required") : undefined
              }
              onChange={(e) => set(field, e.target.value)}
            />
          </div>
        ))}
      </section>

      {/* Section: Aprobaciones Legales */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold">{t("config.section_approvals")}</h3>
        <div className="flex items-center gap-2">
          <Checkbox
            id="cfg-privacy"
            checked={form.privacy_notice_approved}
            onCheckedChange={(v) => set("privacy_notice_approved", v === true)}
          />
          <Label htmlFor="cfg-privacy">{t("config.field.privacy_notice_approved")}</Label>
          <Chip field="privacy_notice_approved" />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="cfg-video-approved"
            checked={form.video_consent_approved}
            onCheckedChange={(v) => set("video_consent_approved", v === true)}
          />
          <Label htmlFor="cfg-video-approved">
            {t("config.field.video_consent_approved")}
          </Label>
        </div>
      </section>

      {/* Section: Configuración del Sistema */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold">{t("config.section_system")}</h3>

        <div className="space-y-1 max-w-xs">
          <Label htmlFor="cfg-retention">{t("config.field.retention_months")}</Label>
          <Input
            id="cfg-retention"
            type="number"
            min={1}
            max={120}
            value={form.retention_months}
            onChange={(e) => set("retention_months", Number(e.target.value))}
          />
          {(form.retention_months < 1 || form.retention_months > 120) && (
            <p role="alert" className="text-sm text-[#dc2626]">
              {t("config.retention_range")}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label htmlFor="cfg-recipients">{t("config.field.data_recipients")}</Label>
          </div>
          <Textarea
            id="cfg-recipients"
            value={form.data_recipients}
            onChange={(e) => set("data_recipients", e.target.value)}
          />
        </div>

        <div className="space-y-1 max-w-md">
          <Label htmlFor="cfg-mode">{t("config.field.mode")}</Label>
          <Select value={cfg.mode} onValueChange={(v) => void handleModeChange(v as "test" | "live")}>
            <SelectTrigger id="cfg-mode" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="test">{t("config.mode_test")}</SelectItem>
              <SelectItem value="live">{t("config.mode_live")}</SelectItem>
            </SelectContent>
          </Select>
          {missing.length > 0 && (
            <div role="alert" className="mt-1 text-sm text-[#dc2626]">
              <p>{t("config.missing_intro")}</p>
              <ul className="list-disc pl-5">
                {missing.map((field) => (
                  <li key={field}>{t(`config.field.${field}`, field)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      <div className="flex gap-3 pt-1">
        <Button
          type="button"
          data-testid="cfg-save-btn"
          disabled={saving}
          onClick={handleSave}
          className="min-h-[44px]"
        >
          {saving ? t("config.saving") : t("config.save")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={saving}
          onClick={handleDiscard}
          className="min-h-[44px]"
        >
          {t("config.discard")}
        </Button>
      </div>
    </div>
  );
}
